import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SideNavigation, Badge } from "@cloudscape-design/components";
import { getCurrentTheme, Theme } from "../../themes/themeConfig";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());

  useEffect(() => {
    const updateTheme = () => {
      setCurrentTheme(getCurrentTheme());
      applySidebarStyles();
    };

    // Apply sidebar styles on mount
    applySidebarStyles();

    // Listen for custom theme change events
    window.addEventListener('themeChanged', updateTheme);
    
    return () => {
      window.removeEventListener('themeChanged', updateTheme);
    };
  }, []);

  const applySidebarStyles = () => {
    const theme = getCurrentTheme();
    
    // Don't apply custom sidebar styling for Black/Default theme
    if (theme.name === 'default') {
      // Remove any custom styling to use CloudScape defaults
      setTimeout(() => {
        const sidebarSelectors = [
          '.awsui-side-navigation',
          '[data-testid="side-navigation"]',
          'nav'
        ];
        
        sidebarSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('background-color');
              element.style.removeProperty('border-right-color');
              element.style.removeProperty('color');
            }
          });
        });

        const itemSelectors = [
          '.awsui-side-navigation-item',
          '.awsui-side-navigation a',
          '.awsui-side-navigation button'
        ];
        
        itemSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('color');
              element.style.removeProperty('background-color');
            }
          });
        });
      }, 100);
      return;
    }
    
    setTimeout(() => {
      // Target sidebar container
      const sidebarSelectors = [
        '.awsui-side-navigation',
        '[data-testid="side-navigation"]',
        'nav'
      ];
      
      sidebarSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('background-color', theme.colors.navigationBackground, 'important');
            element.style.setProperty('border-right-color', theme.colors.navigationBorder, 'important');
            element.style.setProperty('color', theme.colors.navigationText, 'important');
          }
        });
      });

      // Target sidebar items
      const itemSelectors = [
        '.awsui-side-navigation-item',
        '.awsui-side-navigation a',
        '.awsui-side-navigation button'
      ];
      
      itemSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('color', theme.colors.navigationText, 'important');
            element.style.setProperty('background-color', 'transparent', 'important');
          }
        });
      });
    }, 100);
  };

  return (
    <div
      style={{
        height: '100%',
        ...(currentTheme.name !== 'default' && {
          backgroundColor: currentTheme.colors.navigationBackground,
          borderRight: `1px solid ${currentTheme.colors.navigationBorder}`,
          color: currentTheme.colors.navigationText
        })
      }}
    >
      <SideNavigation
        activeHref={location.pathname}
        items={[
          {
            type: "link",
            text: "Home",
            href: "/",
          },
          { type: "divider" },
          {
            type: "section",
            text: "Platform Modules",
            items: [
              { type: "link", text: "Monitor & Alert", href: "/monitor" },
              { type: "link", text: "Intelligent Analysis", href: "/analysis" },
              { type: "link", text: "Report Agent", href: "/reports" },
              {
                type: "link",
                text: "Process Optimization",
                href: "/optimization",
              },
            ],
          },
        ]}
        onFollow={(event) => {
          event.preventDefault();
          navigate(event.detail.href);
        }}
      />
    </div>
  );
};

export default Sidebar;
